import React, { useEffect, useState } from 'react';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';
import { ImportField, ImportFields } from 'components/import/ImportTypes';
import ImportWizard from 'components/import/ImportWizard';
import { parseMkgXml } from 'components/import/functions/parseMkgXml';

const JobImport: React.FC = () => {
    const { handleLogout, environmentType, activeFeatures } = useAuthContext();

    // Mkg xml files are only recognized when the mkg integration is ordered, trials may try it out
    const mkgIntegrationActive = environmentType === 'trial' || activeFeatures.includes('mkg_integration');

    // The job group starts with the required number field and is extended
    // with the importable job model fields fetched from the backend
    const [jobFields, setJobFields] = useState<ImportField[]>([
        { value: 'number', name: 'job.number', required: true },
    ]);

    // Fetch the importable job model fields, derived on the backend from
    // the job model minus its user excluded fields
    useEffect(() => {
        const fetchJobFields = async () => {
            try {
                const response = await fetchData({ apiUrl: 'get_import_fields/job', handleLogout });

                // Build the job group fields from the fetched field names
                if (response && response.fields) {
                    const fetchedFields = response.fields
                        .filter((field: { name: string }) => field.name !== 'number')
                        .map((field: { name: string }) => ({ value: field.name, name: `job.${field.name}` }));

                    setJobFields([
                        { value: 'number', name: 'job.number', required: true },
                        ...fetchedFields,
                    ]);
                }
            } catch (error) {
                console.error('Error fetching job import fields:', error);
            }
        };
        fetchJobFields();
    }, []);

    // Define the import fields
    const importFields: ImportFields = {
        baseFields: [
            {
                job: jobFields,
            },
            {
                client: [
                    { value: 'client_number', name: 'client.client_number' },
                    { value: 'company_name', name: 'client.company_name' },
                    { value: 'contact_person_name', name: 'client.contact_person_name' },
                    { value: 'contact_phone', name: 'client.contact_phone' },
                    { value: 'contact_email', name: 'client.contact_email' },
                ]
            },
            {
                location: [
                    { value: 'street', name: 'location.street' },
                    { value: 'house_number', name: 'location.house_number' },
                    { value: 'house_number_addition', name: 'location.house_number_addition' },
                    { value: 'postal_code', name: 'location.postal_code' },
                    { value: 'city', name: 'location.city' },
                    { value: 'country', name: 'location.country' },
                ]
            },
            {
                item: [
                    { value: 'product_code', name: 'item.product_code' },
                    { value: 'product_name', name: 'item.product_name' },
                    { value: 'line_description', name: 'item.line_description' },
                    { value: 'quantity', name: 'item.quantity' },
                    { value: 'unit', name: 'item.unit' },
                ]
            }
        ]
    };

    return (
        <ImportWizard
            apiObject='job'
            closeUrl='jobs'
            importFields={importFields}
            acceptedFileTypes={mkgIntegrationActive ? ['.xls', '.xlsx', '.csv', '.xml'] : ['.xls', '.xlsx', '.csv']}
            detectPresets={mkgIntegrationActive ? parseMkgXml : undefined}
        />
    );
};

export default JobImport;
